// API bridge client — talks directly to https://api.beyondframesstudio.com
// (no WP proxy, see API_FLOWS.md §3.1). Reads base URL from window.__BF_CONFIG__
// (injected by theme.liquid). One HTTP call per export — retry/polling cadence
// belongs in the orchestrating hook, not here.

const FALLBACK_BRIDGE = 'https://api.beyondframesstudio.com';

export function getBridgeUrl(): string {
  const fromWindow = typeof window !== 'undefined' ? window.__BF_CONFIG__?.bridgeUrl : undefined;
  return (fromWindow || FALLBACK_BRIDGE).replace(/\/+$/, '');
}

export type ApiErrorCode = 'network' | 'timeout' | 'http' | 'parse' | 'abort';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;

  constructor(status: number, code: ApiErrorCode, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export interface GenerateParams {
  files: File[];
  productId: string;
  styleId: string;
  portraitMode: string;
  manaquinUrl?: string;
  sessionToken: string;
  charCount: number;
  /** input_set_code (pet | pet-angle | humanpet | humanpet-angle | default) —
   *  tells the bridge which overlay mode to persist at generate time. */
  inputSetCode?: string;
  // Overlay text — also stored on the job at generate time so a retry can
  // re-apply it even before the client posts the overlay.
  humanName?: string;
  petName?: string;
  petYears?: string;
  petQuote?: string;
  /** Caption modes: single "Family name / message" line burned onto the art. */
  caption?: string;
  /** familypet-caption: chosen clothing-preference id. The bridge maps it to a
   *  clothing instruction appended to the prompt. */
  clothing?: string;
}

export interface GenerateResponse {
  job_id: string;
  /** Alias for `job_id` — populated client-side so React callers can read
   *  either name. The bridge only returns `job_id`; we mirror it. */
  session_id: string;
  status: 'processing' | 'complete' | 'failed';
}

export type JobStatus = 'processing' | 'complete' | 'failed';

export interface StatusResponse {
  status: JobStatus;
  preview_url: string | null;
  final_url: string | null;
  error?: string;
}

/** Generate a portrait. POST /generate with FormData (API_FLOWS.md §3.2 + PRODUCT_DATA_MODEL §11). */
export async function generatePortrait(
  params: GenerateParams,
  signal?: AbortSignal,
): Promise<GenerateResponse> {
  const fd = new FormData();
  params.files.forEach((f) => fd.append('images[]', f));
  fd.append('product_id', params.productId);
  fd.append('style', params.styleId);
  fd.append('portrait_mode', params.portraitMode);
  if (params.manaquinUrl) fd.append('manaquin_url', params.manaquinUrl);
  fd.append('session_token', params.sessionToken);
  fd.append('char_count', String(params.charCount));
  if (params.inputSetCode) fd.append('input_set_code', params.inputSetCode);
  // Overlay text fields (only when non-empty).
  if (params.humanName) fd.append('human_name', params.humanName);
  if (params.petName) fd.append('pet_name', params.petName);
  if (params.petYears) fd.append('pet_years', params.petYears);
  if (params.petQuote) fd.append('pet_quote', params.petQuote);
  if (params.caption) fd.append('caption', params.caption);
  if (params.clothing) fd.append('clothing', params.clothing);

  // Bridge returns { job_id, status }. We mirror job_id → session_id so
  // callers can read either field (the cart property is _session_id).
  const resp = await fetchJson<{ job_id: string; status: GenerateResponse['status'] }>(
    `${getBridgeUrl()}/generate`,
    {
      method: 'POST',
      body: fd,
      headers: { 'X-Session-Token': params.sessionToken },
      signal,
      // Covers the image UPLOAD too — raised so slow/mobile uplinks with large
      // photos don't abort mid-upload while the server still completes the job.
      timeoutMs: 180_000,
    },
  );
  return { ...resp, session_id: resp.job_id };
}

export interface CloneParams {
  sourceJobId: string;
  styleId: string;
  portraitMode: string;
  /** familypet-caption family: chosen clothing-preference id for the retry.
   *  Omitted/empty → bridge inherits the source job's clothing (CloneHandler.php). */
  clothing?: string;
}

/** Retry / clone an existing job with a new style. POST /clone (API_FLOWS.md §3.4). */
export async function clonePortrait(
  params: CloneParams,
  signal?: AbortSignal,
): Promise<GenerateResponse> {
  const resp = await fetchJson<{ job_id: string; status: GenerateResponse['status'] }>(
    `${getBridgeUrl()}/clone`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source_job_id: params.sourceJobId,
        style: params.styleId,
        portrait_mode: params.portraitMode,
        ...(params.clothing ? { clothing: params.clothing } : {}),
      }),
      signal,
      timeoutMs: 30_000,
    },
  );
  return { ...resp, session_id: resp.job_id };
}

export interface CollectEmailParams {
  email: string;
  jobId: string;
  sessionToken: string;
  variantId: number;
  productId: string;
  shopDomain: string;
  /** Formatted price (e.g. "$19.00") shown in the retention email CTA. */
  price?: string;
}

/** Collect customer email for retention. POST /collect-email (API_FLOWS.md §3.6). */
export async function collectEmail(
  params: CollectEmailParams,
  signal?: AbortSignal,
): Promise<{ ok: boolean }> {
  return fetchJson<{ ok: boolean }>(`${getBridgeUrl()}/collect-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: params.email,
      job_id: params.jobId,
      session_token: params.sessionToken,
      variant_id: params.variantId,
      product_id: params.productId,
      shopify_store: params.shopDomain,
      price: params.price,
    }),
    signal,
    timeoutMs: 15_000,
  });
}

/** Poll a job's status. GET /status/{job_id} (bridge Router uses a path param). */
export async function pollStatus(jobId: string, signal?: AbortSignal): Promise<StatusResponse> {
  const url = `${getBridgeUrl()}/status/${encodeURIComponent(jobId)}`;
  return fetchJson<StatusResponse>(url, {
    method: 'GET',
    signal,
    timeoutMs: 15_000,
  });
}

export interface PreviewApiVersion {
  job_id: string;
  preview_url: string | null;
  final_url: string | null;
  style_id: string | null;
  portrait_mode: string;
  status: JobStatus;
  created_at: string | null;
  purchased: boolean;
}

export interface PreviewApiResponse {
  active_job_id: string;
  product_id: string;
  session_token: string;
  is_purchased: boolean;
  versions: PreviewApiVersion[];
}

/**
 * Fetch the full session for a job — every version (generate + retries) plus
 * the product id — so the standalone preview page can rebuild state from just
 * a job_id in the URL. GET /preview/{job_id} (PreviewHandler).
 */
export async function getPreview(jobId: string, signal?: AbortSignal): Promise<PreviewApiResponse> {
  const url = `${getBridgeUrl()}/preview/${encodeURIComponent(jobId)}`;
  return fetchJson<PreviewApiResponse>(url, { method: 'GET', signal, timeoutMs: 15_000 });
}

// ─── Canvas upsell (digital → physical print) ──────────────────────────────

/**
 * Retail (Mode 2) — host a customer-supplied image so the cart line-item can
 * carry a stable URL into fulfilment. POST /canvas-upload (CanvasUploadHandler).
 */
export async function uploadCanvasImage(file: File, signal?: AbortSignal): Promise<{ url: string }> {
  const fd = new FormData();
  fd.append('image', file);
  return fetchJson<{ url: string }>(`${getBridgeUrl()}/canvas-upload`, {
    method: 'POST',
    body: fd,
    signal,
    timeoutMs: 180_000,
  });
}

// ─── Text overlay (memorial name / years / quote burned onto the portrait) ──

export type OverlayMode =
  | 'humanpet'
  | 'humanpet-angle'
  | 'pet'
  | 'pet-angle'
  | 'humanpet-caption'
  | 'smallfamilypet-caption'
  | 'familypet-caption';

export interface OverlayBody {
  job_id: string;
  human_name?: string;
  pet_name?: string;
  pet_years?: string;
  pet_quote?: string;
  /** Caption modes: single "Family name / message" line. */
  caption?: string;
  /** Caption-overlay text color (hex). Omit to keep the bridge's default white. */
  caption_color?: string;
}

/** `{ url }` on success, `{ skipped: true }` when the bridge chose not to overlay. */
export interface OverlayResponse {
  url?: string;
  skipped?: boolean;
}

/**
 * Apply a text overlay to a completed job. Endpoint is mode-specific:
 *   humanpet → /humanpet-overlay, pet-angle → /pet-angle-overlay, etc.
 * Best-effort: callers fall back to the original image on any error / skip.
 */
export async function applyTextOverlay(
  mode: OverlayMode,
  body: OverlayBody,
  signal?: AbortSignal,
): Promise<OverlayResponse> {
  return fetchJson<OverlayResponse>(`${getBridgeUrl()}/${mode}-overlay`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
    timeoutMs: 30_000,
  });
}

/**
 * Re-apply a job's stored overlay in the web process. Used after a clone/retry
 * because the CLI worker often lacks GD and can't burn in the text. The bridge
 * reads the overlay text inherited onto the job — no text needs re-sending.
 */
export async function reapplyOverlay(
  jobId: string,
  signal?: AbortSignal,
): Promise<OverlayResponse> {
  return fetchJson<OverlayResponse>(`${getBridgeUrl()}/reapply-overlay`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ job_id: jobId }),
    signal,
    timeoutMs: 30_000,
  });
}

/** Generate a UUID for session_token (PRODUCT_DATA_MODEL.md §11). */
export function newSessionToken(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Old-browser fallback — RFC4122-ish v4
  return 'bf-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
}

interface FetchOptions extends Omit<RequestInit, 'signal'> {
  timeoutMs?: number;
  signal?: AbortSignal;
}

async function fetchJson<T>(url: string, opts: FetchOptions): Promise<T> {
  const { timeoutMs = 30_000, signal: externalSignal, ...rest } = opts;
  const ctrl = new AbortController();
  const timeoutId = setTimeout(() => ctrl.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs);

  const onExternalAbort = () => ctrl.abort();
  if (externalSignal) {
    if (externalSignal.aborted) ctrl.abort();
    else externalSignal.addEventListener('abort', onExternalAbort);
  }

  let res: Response;
  try {
    res = await fetch(url, { ...rest, signal: ctrl.signal });
  } catch (err) {
    if (ctrl.signal.aborted) {
      const reason = ctrl.signal.reason;
      if (reason instanceof DOMException && reason.name === 'TimeoutError') {
        throw new ApiError(0, 'timeout', `Request to ${url} timed out after ${timeoutMs}ms`);
      }
      throw new ApiError(0, 'abort', 'Request aborted');
    }
    throw new ApiError(0, 'network', err instanceof Error ? err.message : 'Network error');
  } finally {
    clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', onExternalAbort);
  }

  if (!res.ok) {
    let body = '';
    try { body = await res.text(); } catch { /* swallow */ }
    // Prefer the server's own message (JSON {error|message}) so things like the
    // rate-limit notice surface verbatim; fall back to the raw text.
    let serverMsg = '';
    if (body) {
      try {
        const parsed = JSON.parse(body);
        serverMsg = (parsed && (parsed.error || parsed.message)) || '';
      } catch { /* not JSON */ }
    }
    const message = serverMsg || `HTTP ${res.status}${body ? `: ${body.slice(0, 300)}` : ''}`;
    throw new ApiError(res.status, 'http', message);
  }

  try {
    return (await res.json()) as T;
  } catch (err) {
    throw new ApiError(res.status, 'parse', `Invalid JSON from ${url}: ${(err as Error).message}`);
  }
}
